import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import RightArrow from "../../components/SVG";
import signInImg from "../../assets/img/contact/signin.jpg";
import {
  loginStudent,
  sendForgotPasswordOtp,
  resendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resetForgotPassword,
} from "../../services/studentServices";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AdminAuthContext";

const StudentLoginMain = () => {
  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpCountdown, setFpCountdown] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login } = useAuth();

  useEffect(() => {
    if (fpCountdown > 0) {
      const timer = setTimeout(() => setFpCountdown(fpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [fpCountdown]);

  // --- LOGIN HANDLING ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in both fields");
      return;
    }

    try {
      setLoading(true);
      const data = await loginStudent({ email, password });
      login({
        token: data.data?.token,
        email,
        role: "student",
        userId: data.data.student._id,
        expireTime: data.data.expiresIn,
      });
      toast.success("Login successful");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setShowForgot(true);
    setFpStep(1);
    setFpEmail("");
    setFpOtp("");
    setFpNewPassword("");
    setFpConfirmPassword("");
    setFpCountdown(0);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const closeForgot = () => setShowForgot(false);

  const handleSendFpOtp = async (e) => {
    e.preventDefault();
    if (!fpEmail) {
      toast.error("Please enter your email");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fpEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setFpLoading(true);
      await sendForgotPasswordOtp({ email: fpEmail });
      toast.success("OTP sent to your email");
      setFpStep(2);
      setFpCountdown(60);
    } catch (err) {
      toast.error(err.message || "Could not send OTP");
    } finally {
      setFpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setFpLoading(true);
      await resendForgotPasswordOtp({ email: fpEmail });
      toast.success("OTP resent");
      setFpCountdown(60);
    } catch (err) {
      toast.error(err.message || "Resend failed");
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyFpOtp = async (e) => {
    e.preventDefault();
    if (!fpOtp || fpOtp.length !== 6) {
      toast.error("Enter 6-digit OTP");
      return;
    }
    try {
      setFpLoading(true);
      await verifyForgotPasswordOtp({ email: fpEmail, otp: fpOtp });
      toast.success("OTP Verified! Now set your new password");
      setFpStep(3);
    } catch (err) {
      toast.error(err.message || "OTP invalid");
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Validation
    if (!fpNewPassword || !fpConfirmPassword) {
      toast.error("Please fill in both password fields");
      return;
    }

    if (fpNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    if (fpNewPassword !== fpConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setFpLoading(true);
      await resetForgotPassword({
        email: fpEmail,
        password: fpNewPassword,
        confirmPassword: fpConfirmPassword,
      });
      toast.success("Password reset successful! You can now login");
      setShowForgot(false);
      // Optionally reset login form with the email
      setEmail(fpEmail);
    } catch (err) {
      toast.error(err.message || "Failed to reset password");
    } finally {
      setFpLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (password.length < 6) return { level: "weak", color: "#dc3545" };
    if (password.length < 8) return { level: "medium", color: "#ffc107" };
    if (
      password.match(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
      )
    ) {
      return { level: "strong", color: "#28a745" };
    }
    return { level: "medium", color: "#ffc107" };
  };

  const passwordStrength = getPasswordStrength(fpNewPassword);

  return (
    <main>
      <div className="it-signup-area">
        <div className="container">
          <div className="p-relative">
            <div className="it-signup-thumb d-none d-lg-block">
              <img src={signInImg} alt="Sign In" />
            </div>
            <div className="row">
              <div className="col-xl-6 col-lg-6">
                <form onSubmit={handleSubmit}>
                  <div className="it-signup-wrap">
                    <h4 className="it-signup-title">
                      Student Membership Login
                    </h4>
                    <div className="it-signup-input-wrap">
                      <div className="it-signup-input mb-20">
                        <input
                          type="email"
                          placeholder="Email *"
                          value={email}
                          onChange={(e) =>
                            setEmail(e.target.value.toLowerCase())
                          }
                          style={{
                            border: "1px solid black",
                            borderRadius: "10px",
                            textTransform: "lowercase",
                          }}
                        />
                      </div>
                      <div className="it-signup-input mb-20">
                        <input
                          type="password"
                          placeholder="Password *"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          style={{
                            border: "1px solid black",
                            borderRadius: "10px",
                          }}
                        />
                      </div>
                    </div>
                    <div className="it-signup-forget d-flex justify-content-between flex-wrap">
                      <a
                        className="mb-20"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openForgot();
                        }}
                        style={{ color: "#007bff", cursor: "pointer" }}
                      >
                        Forgot your password?
                      </a>
                    </div>
                    <div className="it-signup-btn d-sm-flex justify-content-between align-items-center mb-40">
                      <button
                        type="submit"
                        className="ed-btn-theme"
                        disabled={loading}
                      >
                        {loading ? "Logging in..." : "Login in"}
                        <i>
                          <RightArrow />
                        </i>
                      </button>
                    </div>
                    <div className="it-signup-text text-center">
                      <span>
                        Don't have an account yet?{" "}
                        <Link to="/student-register">Sign Up</Link>
                      </span>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showForgot && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            zIndex: 9999,
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={closeForgot}
        >
          <div
            className="modal-content"
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: "14px",
              padding: "30px",
              width: "95%",
              maxWidth: fpStep === 3 ? 450 : 400,
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 12px 32px #21244a1f",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* X Close button */}
            <button
              onClick={closeForgot}
              style={{
                position: "absolute",
                top: 8,
                right: 14,
                fontSize: 24,
                background: "none",
                border: "none",
                color: "#888",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              ×
            </button>

            {/* Progress indicator */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: fpStep >= step ? "#4424a7" : "#ddd",
                      color: fpStep >= step ? "#fff" : "#888",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: "bold",
                    }}
                  >
                    {step}
                  </div>
                ))}
              </div>
              <div
                style={{
                  textAlign: "center",
                  marginTop: 8,
                  fontSize: 12,
                  color: "#666",
                }}
              >
                {fpStep === 1 && "Enter Email"}
                {fpStep === 2 && "Verify OTP"}
                {fpStep === 3 && "Reset Password"}
              </div>
            </div>

            {/* Step 1: send OTP */}
            {fpStep === 1 && (
              <form onSubmit={handleSendFpOtp}>
                <h5 style={{ marginBottom: 10 }}>Forgot Password</h5>
                <div style={{ marginBottom: 12, color: "#444" }}>
                  Enter your email to receive an OTP
                </div>
                <input
                  type="email"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  placeholder="Email address"
                  disabled={fpLoading}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: 15,
                    marginBottom: 20,
                  }}
                />
                <button
                  type="submit"
                  disabled={fpLoading}
                  style={{
                    background: fpLoading ? "#ccc" : "#4424a7",
                    color: "#fff",
                    fontWeight: 500,
                    padding: "10px 0",
                    borderRadius: 7,
                    border: "none",
                    width: "100%",
                    fontSize: 16,
                    cursor: fpLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {fpLoading ? "Sending..." : "Send OTP"}
                </button>
              </form>
            )}

            {/* Step 2: verify OTP */}
            {fpStep === 2 && (
              <form onSubmit={handleVerifyFpOtp}>
                <h5>Enter OTP</h5>
                <div style={{ marginBottom: 10, fontSize: 15 }}>
                  Sent to <b>{fpEmail}</b>
                </div>
                <input
                  type="text"
                  maxLength={6}
                  value={fpOtp}
                  onChange={(e) =>
                    setFpOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="6-digit OTP"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: 20,
                    textAlign: "center",
                    letterSpacing: "2px",
                    marginBottom: 18,
                  }}
                  disabled={fpLoading}
                />
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  {fpCountdown > 0 ? (
                    <span style={{ color: "#888" }}>
                      {`Resend OTP in ${fpCountdown}s`}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={fpLoading}
                      style={{
                        border: "none",
                        background: "none",
                        color: "#4424a7",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setFpStep(1);
                      setFpOtp("");
                      setFpCountdown(0);
                    }}
                    style={{
                      flex: 1,
                      background: "#888",
                      color: "#fff",
                      padding: "10px 0",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={fpLoading || fpOtp.length !== 6}
                    style={{
                      flex: 1,
                      background:
                        fpLoading || fpOtp.length !== 6 ? "#ccc" : "#4424a7",
                      color: "#fff",
                      padding: "10px 0",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 16,
                      cursor:
                        fpLoading || fpOtp.length !== 6
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {fpLoading ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Reset Password */}
            {fpStep === 3 && (
              <form onSubmit={handleResetPassword}>
                <h5>Reset Password</h5>
                <div style={{ marginBottom: 15, fontSize: 15 }}>
                  Set a new password for <b>{fpEmail}</b>
                </div>

                {/* New Password */}
                <div style={{ marginBottom: 15 }}>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={fpNewPassword}
                      onChange={(e) => setFpNewPassword(e.target.value)}
                      placeholder="New Password *"
                      disabled={fpLoading}
                      style={{
                        width: "100%",
                        padding: "10px 40px 10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        fontSize: 15,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#666",
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {showNewPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  {fpNewPassword && (
                    <div style={{ marginTop: 5, fontSize: 12 }}>
                      <span style={{ color: passwordStrength.color }}>
                        Password strength: {passwordStrength.level}
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={fpConfirmPassword}
                      onChange={(e) => setFpConfirmPassword(e.target.value)}
                      placeholder="Confirm Password *"
                      disabled={fpLoading}
                      style={{
                        width: "100%",
                        padding: "10px 40px 10px 12px",
                        borderRadius: "8px",
                        border: `1px solid ${
                          fpConfirmPassword &&
                          fpNewPassword !== fpConfirmPassword
                            ? "#dc3545"
                            : "#ddd"
                        }`,
                        fontSize: 15,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#666",
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {showConfirmPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  {fpConfirmPassword && fpNewPassword !== fpConfirmPassword && (
                    <div
                      style={{ marginTop: 5, fontSize: 12, color: "#dc3545" }}
                    >
                      Passwords do not match
                    </div>
                  )}
                </div>

                {/* Password Requirements */}
                <div style={{ marginBottom: 20, fontSize: 12, color: "#666" }}>
                  Password requirements:
                  <ul style={{ margin: "5px 0", paddingLeft: 20 }}>
                    <li
                      style={{
                        color:
                          fpNewPassword.length >= 6 ? "#28a745" : "#dc3545",
                      }}
                    >
                      At least 6 characters
                    </li>
                    <li
                      style={{
                        color: fpNewPassword.match(/[A-Z]/)
                          ? "#28a745"
                          : "#dc3545",
                      }}
                    >
                      One uppercase letter
                    </li>
                    <li
                      style={{
                        color: fpNewPassword.match(/[0-9]/)
                          ? "#28a745"
                          : "#dc3545",
                      }}
                    >
                      One number
                    </li>
                  </ul>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setFpStep(2);
                      setFpNewPassword("");
                      setFpConfirmPassword("");
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                    style={{
                      flex: 1,
                      background: "#888",
                      color: "#fff",
                      padding: "10px 0",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={
                      fpLoading ||
                      !fpNewPassword ||
                      !fpConfirmPassword ||
                      fpNewPassword !== fpConfirmPassword ||
                      fpNewPassword.length < 6
                    }
                    style={{
                      flex: 1,
                      background:
                        fpLoading ||
                        !fpNewPassword ||
                        !fpConfirmPassword ||
                        fpNewPassword !== fpConfirmPassword ||
                        fpNewPassword.length < 6
                          ? "#ccc"
                          : "#4424a7",
                      color: "#fff",
                      padding: "10px 0",
                      border: "none",
                      borderRadius: 7,
                      fontSize: 16,
                      cursor:
                        fpLoading ||
                        !fpNewPassword ||
                        !fpConfirmPassword ||
                        fpNewPassword !== fpConfirmPassword ||
                        fpNewPassword.length < 6
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {fpLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default StudentLoginMain;
